import { useState, useMemo } from 'react';

/**
 * Custom hook for managing store filtering functionality
 * @param {Array} stores - Array of store objects
 * @param {Array} validationResults - Array of validation result objects
 * @returns {Object} Filtering state and filtered stores
 */
export function useStoreFiltering(stores = [], validationResults = []) {
    const [filterStatus, setFilterStatus] = useState('all');

    /**
     * Get filtered stores based on validation status
     * @returns {Array} Filtered array of stores
     */
    const filteredStores = useMemo(() => {
        if (filterStatus === 'all') return stores;

        return stores.filter(store => {
            const result = validationResults.find(r => r.storeId === store._id);
            if (!result) return filterStatus === 'not_validated';

            const score = result.overallScore || 0;
            switch (filterStatus) {
                case 'excellent': return score >= 90;
                case 'good': return score >= 70 && score < 90;
                case 'warning': return score >= 50 && score < 70;
                case 'critical': return score < 50;
                default: return true;
            }
        });
    }, [stores, validationResults, filterStatus]);

    /**
     * Apply status filter to stores
     * @param {string} status - Filter status ('all', 'excellent', 'good', 'warning', 'critical', 'not_validated')
     */
    const applyStatusFilter = (status) => {
        setFilterStatus(status);
    };

    /**
     * Reset filter to show all stores
     */
    const resetFilter = () => {
        setFilterStatus('all');
    };

    /**
     * Get stores by specific validation score range
     * @param {number} minScore - Minimum score threshold
     * @param {number} maxScore - Maximum score threshold
     * @returns {Array} Filtered stores
     */
    const getStoresByScoreRange = (minScore, maxScore) => {
        return stores.filter(store => {
            const result = validationResults.find(r => r.storeId === store._id);
            if (!result) return false;

            const score = result.overallScore || 0;
            return score >= minScore && score <= maxScore;
        });
    };

    return {
        filterStatus,
        filteredStores,
        applyStatusFilter,
        setFilterStatus,
        resetFilter,
        getStoresByScoreRange,
        // Convenience getters
        excellentStores: getStoresByScoreRange(90, 100),
        goodStores: getStoresByScoreRange(70, 89),
        warningStores: getStoresByScoreRange(50, 69),
        criticalStores: getStoresByScoreRange(0, 49),
        unvalidatedStores: stores.filter(store => !validationResults.some(r => r.storeId === store._id))
    };
}

export default useStoreFiltering;
